import Service, { inject as service } from '@ember/service';
import { isPresent } from '@ember/utils';
import { task } from 'ember-concurrency';

import { UploadCredentialsError } from 'later/errors/upload-credentials';
import attachmentData from 'later/utils/generic-media-attachment';
import { isGif, isImage, isVideo } from 'later/utils/media-item-upload/check-file-type';

import type AlertsService from './alerts';
import type AuthService from './auth';
import type { AsyncBelongsTo } from '@ember-data/model';
import type StoreService from '@ember-data/store';
import type IntlService from 'ember-intl/services/intl';
import type AccountModel from 'later/models/account';
import type GenericAttachmentModel from 'later/models/generic-attachment';
import type MediaKitAttachmentModel from 'later/models/media-kit-attachment';
import type PartnershipsAttachmentModel from 'later/models/partnerships-attachment';
import type UploadCredentialsService from 'later/services/upload-credentials';
import type { UploadTarget, UploadTargetParams } from 'later/services/upload-credentials';
import type { UntypedService } from 'shared/types';
import type { Variant } from 'shared/types/generic-media';

type GenericAttachmentModelWithPreviewUrl = MediaKitAttachmentModel | PartnershipsAttachmentModel;

interface UploadImageArgs {
  attachmentType: string;
  setPreview?: (arg: string) => void;
  setUploadProgress?: (progress: number) => void;
  recordId?: number;
  additionalModelData?: { [key: string]: any };
  credentialsTarget: CredentialsTarget;
}

/** The resource the upload is for, and the account that owns it, whose flag picks STS over Cognito */
export interface CredentialsTarget {
  account: AsyncBelongsTo<AccountModel> | undefined;
  params: UploadTargetParams;
}

export default class GenericMediaManagerService extends Service {
  @service declare alerts: AlertsService;
  @service declare auth: AuthService;
  @service declare intl: IntlService;
  @service declare mediaUpload: UntypedService;
  @service declare store: StoreService;
  @service declare uploadCredentials: UploadCredentialsService;

  /**
   * The bucket within S3 to send the upload to
   */
  processingBucket = 'later-incoming';

  /**
   * The maximum size of an image a user can upload in bytes
   */
  get maxImageSize(): number {
    return this.auth.currentGroup.account.get('maxImageFileSize') || this.auth.currentAccount.maxImageFileSize;
  }

  /**
   * The maximum size of an image a user can upload in MB
   */
  get maxImageSizeMB(): number {
    const bytesInKB = 1024;
    const KBInMB = 1024;
    return this.maxImageSize / KBInMB / bytesInKB;
  }

  /**
   * This returns a promise that resolves if the given url for an image attachment loads properly
   */
  checkLoaded(attachment: Variant): Promise<void> {
    return new Promise((resolve) => {
      const image = new Image();
      image.onload = function () {
        resolve();
      };
      image.src = attachment.url;
    });
  }

  /**
   * This extracts the URL of an uploaded image prior to the attachment being saved and
   * passes it to a function that will accept the URL for display.
   */
  showPreview(file: File, setPreview: (arg: string) => void): void {
    const reader = new FileReader();

    reader.addEventListener(
      'load',
      () => {
        if (typeof reader.result === 'string') {
          setPreview(reader.result);
        }
      },
      false
    );
    reader.readAsDataURL(file);
  }

  /**
   * returns a Promise that resolves when all the attachment URLs that have been processed on the backend
   * load correctly when displayed
   */
  async loadAttachments(attachments: GenericAttachmentModel[]): Promise<void> {
    const processedAttachments = attachments.flatMap(({ variants }) =>
      Object.values(variants).filter((attachment) => attachment.processed)
    );
    await Promise.all(processedAttachments.map((attachment) => this.checkLoaded(attachment)));
  }

  /**
   * delete local ember data records after saving a record with a hasMany GenericAttachment relationship.
   *
   * As we do not save the attached records directly and instead rely on the BE when saving the parent record
   * Ember has no way of updating the locally created attachments used for previewing the uploaded media.
   * This results in duplicates after saving requiring manually clearing local versions of records.
   *
   */
  mergeUploadedAttachments<T extends GenericAttachmentModelWithPreviewUrl>(images: T[]): void {
    const returnedImages = new Map();
    const existingImages: T[] = [];

    images.forEach((img) => {
      if (isPresent(img.id)) {
        returnedImages.set(img.key, img);
      } else {
        existingImages.push(img);
      }
    });

    existingImages.forEach((img) => {
      // NOTE: key field returned from BE is missing the file extension and any user prefix
      const returnedImg = returnedImages.get(img.key.split('.')[0]?.split('/').pop());
      if (returnedImg) returnedImg.set('previewUrl', img.previewUrl);
      this.store.unloadRecord(img);
    });
  }

  /**
   * Handles file size validation and uploading to S3
   */
  uploadImage = task(
    { drop: true },
    async (
      file: File,
      {
        attachmentType,
        setPreview,
        setUploadProgress,
        recordId,
        additionalModelData,
        credentialsTarget
      }: UploadImageArgs
    ) => {
      if (!file) {
        return;
      }

      if (this.#getHasMediaTypeError(file)) {
        this.#showMediaTypeError();
        return;
      }

      if (this.#getHasImageSizeError(file)) {
        this.#showImageSizeError();
        return;
      }

      const account = await credentialsTarget.account;
      if (!account) {
        // The flag is unknown, not off, so neither uploader can be chosen safely.
        throw new UploadCredentialsError('invalid_target');
      }
      const { bucket, keyPrefix, clientOptions }: UploadTarget = account.rolloutStsUploads
        ? await this.uploadCredentials.fetchTarget(credentialsTarget.params)
        : { bucket: this.processingBucket, keyPrefix: '' };
      const s3Key = this.mediaUpload.generateProcessingKey(file, false, keyPrefix);
      await this.mediaUpload.uploadToS3.perform(file, bucket, s3Key, setUploadProgress, clientOptions);

      if (setPreview) {
        this.showPreview(file, setPreview);
      }

      const { model, modelData } = attachmentData(recordId, additionalModelData)[attachmentType];
      return this.store.createRecord(model, {
        ...modelData,
        contentType: file.type,
        key: s3Key,
        processingBucket: bucket
      });
    }
  );

  #getHasImageSizeError(file: File): boolean {
    return file.size > this.maxImageSize;
  }

  #getHasMediaTypeError(file: File): boolean {
    return isGif(file) || isVideo(file) || !isImage(file);
  }

  #showImageSizeError(): void {
    this.alerts.alert(this.intl.t('alerts.generic_media.file_too_large', { maxSize: this.maxImageSizeMB }), {
      title: this.intl.t('alerts.media_items.new.image_limit_over.title')
    });
  }

  #showMediaTypeError(): void {
    this.alerts.alert(this.intl.t('alerts.generic_media.file_type_not_supported.description'), {
      title: this.intl.t('alerts.generic_media.file_type_not_supported.title')
    });
  }
}

declare module '@ember/service' {
  interface Registry {
    'generic-media-manager': GenericMediaManagerService;
  }
}
